import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SCHOOL_LOGO, SCHOOL_NAME, ROLE_HOME } from "@/lib/api";
import { AlertCircle, LogIn, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user !== false) {
    return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await login(username, password);
    setBusy(false);
    if (res.ok) navigate(ROLE_HOME[res.user.role] || "/", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* decorative blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-indigo-400/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-violet-400/30 rounded-full blur-3xl" />

      <div className="relative z-10 flex flex-col items-center mb-8">
        <div className="w-28 h-28 bg-white rounded-full p-3 shadow-2xl shadow-indigo-900/30 mb-5">
          <img src={SCHOOL_LOGO} alt="Logo SLB Pelita Nusa" className="w-full h-full object-contain" />
        </div>
        <h1 className="text-4xl font-extrabold text-white tracking-tight">Portal PENA</h1>
        <p className="text-indigo-200 mt-1 text-sm">{SCHOOL_NAME}</p>
      </div>

      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 sm:p-10">
        <h2 className="text-2xl font-bold text-slate-900 text-center mb-1">Login</h2>
        <p className="text-sm text-slate-500 text-center mb-6">Masuk dengan akun Anda</p>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="pp-label" htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              className="pp-input"
              placeholder="username"
              data-testid="login-username-input"
            />
          </div>
          <div>
            <label className="pp-label" htmlFor="password">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="pp-input pr-12"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                aria-label={showPassword ? "Sembunyikan password" : "Lihat password"}
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-testid="remember-checkbox" />
            Ingat saya
          </label>

          {error && (
            <div className="flex items-start gap-2 p-3 border border-rose-200 bg-rose-50 text-rose-700 text-sm rounded-lg" data-testid="login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full pp-btn-primary py-3 text-base">
            {busy ? "Memproses…" : (<><LogIn className="w-4 h-4" /> Login</>)}
          </button>
        </form>

        <div className="mt-6 pt-5 border-t border-slate-100 text-center text-xs text-slate-500 leading-relaxed">
          Aplikasi ini hanya untuk <b>Guru</b> dan <b>Orangtua siswa SLB Pelita Nusa</b>.<br />
          Silakan hubungi <b>Admin</b> untuk permintaan login.
        </div>
      </div>

      <div className="relative z-10 mt-6 text-center text-xs text-indigo-200">
        © 2026 <b>Portal PENA</b>. All rights reserved.
      </div>
    </div>
  );
}
